import { defineStore } from 'pinia'

interface Toast {
    id: string
    type: 'success' | 'error' | 'info' | 'warning'
    title: string
    message?: string
    duration?: number
}

export const useToastStore = defineStore('toast', () => {
    const toasts = ref<Toast[]>([])

    function addToast(toast: Omit<Toast, 'id'>) {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
        const newToast: Toast = {
            id,
            ...toast
        }
        toasts.value.push(newToast)

        // Auto-remove after duration (default 5 seconds)
        const duration = toast.duration ?? 5000
        if (duration > 0) {
            setTimeout(() => {
                removeToast(id)
            }, duration)
        }

        return id
    }

    function removeToast(id: string) {
        toasts.value = toasts.value.filter((toast) => toast.id !== id)
    }

    function showSuccess(title: string, message?: string, duration?: number) {
        return addToast({ type: 'success', title, message, duration })
    }

    function showError(title: string, message?: string, duration?: number) {
        // Errors require manual dismissal by default (duration = 0 means no auto-dismiss)
        return addToast({ type: 'error', title, message, duration: duration ?? 0 })
    }

    function showInfo(title: string, message?: string, duration?: number) {
        return addToast({ type: 'info', title, message, duration })
    }

    function showWarning(title: string, message?: string, duration?: number) {
        return addToast({ type: 'warning', title, message, duration })
    }

    function clearAll() {
        toasts.value = []
    }

    return {
        toasts: computed(() => toasts.value),
        addToast,
        removeToast,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        clearAll
    }
})
